/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { config } from "config";
import { useConfig } from "config/useConfig";
import { useEffect, useState } from "react";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */


export const useMsalConfig = () => {
    const [msalConfig, setMsalConfig] = useState(null)
    const { aadAppReactClientId } = useConfig();

    useEffect(() => {
        if (aadAppReactClientId)
            setMsalConfig({
                auth: {
                    clientId: aadAppReactClientId,
                    authority: "https://login.microsoftonline.com/common",
                    redirectUri: window.location.origin + "/",
                },
                cache: {
                    cacheLocation: "sessionStorage", // This configures where your cache will be stored
                    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
                },
                system: {
                    loggerOptions: {
                        loggerCallback: (level, message, containsPii) => {

                            if (containsPii) {
                                return;
                            }
                            switch (level) {
                                case LogLevel.Error:
                                    console.error(message);
                                    return;
                                case LogLevel.Info:
                                    console.info(message);
                                    return;
                                case LogLevel.Verbose:
                                    console.debug(message);
                                    return;
                                case LogLevel.Warning:
                                    console.warn(message);
                                    return;
                                default:
                                    console.log(message);
                                    break;
                            }
                        }
                    }
                }
            })

        return () => {
            setMsalConfig(null)
        }
    }, [aadAppReactClientId])

    return msalConfig;
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
// export const loginRequest = {
//     scopes: ["api://b5fdc4d2-fe55-422f-a40b-3980e42e87e5/access_as_user"]
// };

export const useLoginRequest = () => {
    const { aadAppApiClientId } = useConfig();

    return {
        scopes: [`api://${aadAppApiClientId}/access_as_user`]
    }
}

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
    changeClient: `${config.apiUrl}/accounts/change-client`,
    authenticateWithMsJwt: `${config.apiUrl}/accounts/authenticate-with-ms`
};
